import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismBanner } from '@mylighthouse/prism-ember';
import type { BannerStyle } from '@mylighthouse/prism-ember/types/components/prism/banner';

interface GeneralDismissibleBannerSignature {
  Args: {
    bannerMessage: string;
    bannerStyle?: BannerStyle;
    closeBanner: () => void;
    dismissText?: string;
    dontShowAgainText?: string;
    hasCtaButtons?: boolean;
    intercomArticleLink?: string;
    intercomLinkText?: string;
    onDismiss: (dismiss: boolean) => void;
    shouldShowIntercomLink?: boolean;
  };
}

export default class GeneralDismissibleBanner extends Component<GeneralDismissibleBannerSignature> {
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get bannerStyle() {
    return this.args.bannerStyle ?? 'info';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get checkboxAction() {
    return {
      label: this.dontShowAgainText,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      action: () => this.handleDismissClicked(true),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get closeAction() {
    return this.args.closeBanner;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dismissText() {
    return this.args.dismissText || this.intl.t('general.got_it');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get dontShowAgainText() {
    return (
      this.args.dontShowAgainText || this.intl.t('general.dont_show_again')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get intercomLinkText() {
    return this.args.intercomLinkText || this.intl.t('general.learn_more');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get link() {
    if (!this.args.shouldShowIntercomLink) {
      return undefined;
    }
    return {
      text: this.intercomLinkText,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      action: () =>
        this.intercom.redirectToIntercomHelp(this.args.intercomArticleLink),
      icon: true,
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get primaryAction() {
    return {
      label: this.dismissText,
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      action: () => this.handleDismissClicked(),
    };
  }

  @action handleDismissClicked(dismiss = false): void {
    this.args.onDismiss(dismiss);
  }

  <template>
    <PrismBanner
      @bannerMessage={{@bannerMessage}}
      @bannerStyle={{this.bannerStyle}}
      @checkbox={{this.checkboxAction}}
      @close={{@closeBanner}}
      @hasCta={{@hasCtaButtons}}
      @link={{this.link}}
      @primaryAction={{this.primaryAction}}
    />
  </template>
}
